/**
 * Hall map dropdown session lock (CVF-94).
 *
 * Per browser tab + show: the first map view records whether MAP_SELECT allows the
 * hall selector. Later menu hops reuse that value instead of per-path jwt decode
 * (MAP_SELECT can drift between routes such as exhibitor_exhibit_map vs floor_map).
 *
 * Hard locks (MAP_KEY, public ?map=) always hide the selector on every visit.
 */

const MAP_SELECTOR_SESSION_VISIBLE = '1';
const MAP_SELECTOR_SESSION_HIDDEN = '0';

/** Storage key scoped to this tab and show (show_key or public site path prefix). */
export const getMapSelectorSessionScopeKey = (showKey, pathname = '') => {
    if (showKey) {
        return `maps_v3_map_select_${showKey}`;
    }
    const parts = (pathname || '').split('/').filter(Boolean);
    if (parts.length >= 2) {
        return `maps_v3_map_select_path_${parts[0]}_${parts[1]}`;
    }
    if (parts.length >= 1) {
        return `maps_v3_map_select_path_${parts[0]}`;
    }
    return 'maps_v3_map_select_default';
};

export const readMapSelectorSessionLock = (scopeKey) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return null;
        }
        const v = window.sessionStorage.getItem(scopeKey);
        if (v === MAP_SELECTOR_SESSION_VISIBLE) {
            return true;
        }
        if (v === MAP_SELECTOR_SESSION_HIDDEN) {
            return false;
        }
        return null;
    } catch (e) {
        return null;
    }
};

export const writeMapSelectorSessionLock = (scopeKey, visible) => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return;
        }
        window.sessionStorage.setItem(
            scopeKey,
            visible ? MAP_SELECTOR_SESSION_VISIBLE : MAP_SELECTOR_SESSION_HIDDEN
        );
    } catch (e) {
        /* quota / private mode — fall back to live SALES only */
    }
};

/** MAP_SELECT only: whether SALES config allows the hall selector (before MAP_KEY / ?map=). */
export const mapSelectorAllowedByMapSelect = (sales) => {
    if (!sales) {
        return true;
    }
    return sales.MAP_SELECT !== 'hide';
};

/** Single-map locks: always hide selector on every visit regardless of session. */
export const mapSelectorForcedHidden = (sales, urlToken, qMap) => {
    if (sales?.MAP_KEY) {
        return true;
    }
    return !!(urlToken && qMap);
};

/**
 * Resolve hall map dropdown visibility for MainViewer.
 *
 * @returns {{
 *   show: boolean,
 *   scopeKey: string,
 *   sessionLocked: boolean,
 *   sessionVisible: boolean|null,
 *   mapSelectLive: boolean,
 *   forcedHidden: boolean,
 * }}
 */
export const resolveMapSelectorShow = ({ sales, urlToken, qMap, showKey, pathname }) => {
    const scopeKey = getMapSelectorSessionScopeKey(showKey, pathname);
    const mapSelectLive = mapSelectorAllowedByMapSelect(sales);
    const forcedHidden = mapSelectorForcedHidden(sales, urlToken, qMap);

    if (forcedHidden) {
        let sessionVisible = readMapSelectorSessionLock(scopeKey);
        if (sessionVisible === null) {
            sessionVisible = false;
            writeMapSelectorSessionLock(scopeKey, false);
        }
        return {
            show: false,
            scopeKey,
            sessionLocked: true,
            sessionVisible,
            mapSelectLive,
            forcedHidden: true,
        };
    }

    let sessionVisible = readMapSelectorSessionLock(scopeKey);
    if (sessionVisible === null) {
        sessionVisible = mapSelectLive;
        writeMapSelectorSessionLock(scopeKey, sessionVisible);
    }

    return {
        show: sessionVisible,
        scopeKey,
        sessionLocked: true,
        sessionVisible,
        mapSelectLive,
        forcedHidden: false,
    };
};
